import React, { Children } from 'react';
import { Section } from '../components/Section';
import { Shield, Lock, Database, Eye, FileText, AlertCircle } from 'lucide-react';
export function PrivacyPolicy() {
  return <div className="pt-20" data-id="element-370">
      <Section className="bg-gradient-to-br from-[#EEF2FF] to-white" data-id="element-371">
        <div className="container mx-auto px-4 max-w-4xl" data-id="element-372">
          <div className="text-center mb-12" data-id="element-373">
            <Shield className="w-16 h-16 text-[#6366F1] mx-auto mb-6" data-id="element-374" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-id="element-375">
              Privacy Policy
            </h1>
            <p className="text-gray-600" data-id="element-376">Last Updated: December 17, 2024</p>
          </div>
        </div>
      </Section>

      <Section className="bg-white" data-id="element-377">
        <div className="container mx-auto px-4 max-w-4xl" data-id="element-378">
          <div className="prose prose-lg max-w-none" data-id="element-379">
            {/* Overview */}
            <div className="mb-12" data-id="element-380">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-381">
                Overview
              </h2>
              <p className="text-gray-600 leading-relaxed" data-id="element-382">
                Cart Tracker is a browser extension that helps users track their
                e-commerce purchases by capturing cart data from checkout pages.
                This privacy policy explains what data we collect, how we use
                it, and your rights regarding your data.
              </p>
            </div>

            {/* Data Collection */}
            <div className="mb-12" data-id="element-383">
              <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-384">
                Data Collection
              </h2>

              <div className="bg-blue-50 border-l-4 border-[#6366F1] p-6 mb-8" data-id="element-385">
                <div className="flex items-start gap-3" data-id="element-386">
                  <Database className="w-6 h-6 text-[#6366F1] flex-shrink-0 mt-1" data-id="element-387" />
                  <div data-id="element-388">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-389">
                      Data Collected Locally (Stored in Your Browser)
                    </h3>
                    <p className="text-gray-700 mb-4" data-id="element-390">
                      The following data is stored locally in your browser using
                      Chrome's storage API and{' '}
                      <strong data-id="element-391">never leaves your device</strong> unless you
                      explicitly use the AI extraction feature:
                    </p>
                    <ul className="space-y-2 text-gray-700" data-id="element-392">
                      <li data-id="element-393">
                        <strong data-id="element-394">Purchase Records:</strong> Product names,
                        quantities, prices, totals, and order details captured
                        from checkout pages
                      </li>
                      <li data-id="element-395">
                        <strong data-id="element-396">Site Information:</strong> Website hostname
                        where the purchase was made
                      </li>
                      <li data-id="element-397">
                        <strong data-id="element-398">Timestamps:</strong> Date and time of each
                        captured purchase
                      </li>
                      <li data-id="element-399">
                        <strong data-id="element-400">Custom Field Values:</strong> Any custom fields
                        you configure (e.g., PO numbers, project codes)
                      </li>
                      <li data-id="element-401">
                        <strong data-id="element-402">User Preferences:</strong> Settings such as HTML
                        parsing preferences
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 border-l-4 border-[#EC4899] p-6 mb-8" data-id="element-403">
                <div className="flex items-start gap-3" data-id="element-404">
                  <Lock className="w-6 h-6 text-[#EC4899] flex-shrink-0 mt-1" data-id="element-405" />
                  <div data-id="element-406">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-407">
                      Data Collected When Using AI Extraction Features
                    </h3>
                    <p className="text-gray-700 mb-4" data-id="element-408">
                      When you <strong data-id="element-409">explicitly choose</strong> to use the
                      AI-powered extraction features (Screenshot Capture or HTML
                      Parsing), the following data is sent to our backend
                      servers:
                    </p>
                    <div className="overflow-x-auto" data-id="element-410">
                      <table className="min-w-full bg-white border border-gray-200 rounded-lg" data-id="element-411">
                        <thead className="bg-gray-50" data-id="element-412">
                          <tr data-id="element-413">
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b" data-id="element-414">
                              Data Type
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b" data-id="element-415">
                              Purpose
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b" data-id="element-416">
                              Retention
                            </th>
                          </tr>
                        </thead>
                        <tbody className="text-sm text-gray-700" data-id="element-417">
                          <tr className="border-b" data-id="element-418">
                            <td className="px-4 py-3" data-id="element-419">Page URL</td>
                            <td className="px-4 py-3" data-id="element-420">
                              Identify the e-commerce site
                            </td>
                            <td className="px-4 py-3" data-id="element-421">
                              Not stored after processing
                            </td>
                          </tr>
                          <tr className="border-b" data-id="element-422">
                            <td className="px-4 py-3" data-id="element-423">
                              Page Screenshot (JPEG)
                            </td>
                            <td className="px-4 py-3" data-id="element-424">
                              AI vision analysis to extract cart data
                            </td>
                            <td className="px-4 py-3" data-id="element-425">
                              Not stored after processing
                            </td>
                          </tr>
                          <tr className="border-b" data-id="element-426">
                            <td className="px-4 py-3" data-id="element-427">Page HTML (cleaned)</td>
                            <td className="px-4 py-3" data-id="element-428">
                              AI text analysis to extract cart data
                            </td>
                            <td className="px-4 py-3" data-id="element-429">
                              Not stored after processing
                            </td>
                          </tr>
                          <tr className="border-b" data-id="element-430">
                            <td className="px-4 py-3" data-id="element-431">Email Address</td>
                            <td className="px-4 py-3" data-id="element-432">
                              Account authentication
                            </td>
                            <td className="px-4 py-3" data-id="element-433">
                              Stored until account deletion
                            </td>
                          </tr>
                          <tr className="border-b" data-id="element-434">
                            <td className="px-4 py-3" data-id="element-435">Encrypted API Key</td>
                            <td className="px-4 py-3" data-id="element-436">
                              Access to Anthropic Claude API
                            </td>
                            <td className="px-4 py-3" data-id="element-437">
                              Stored encrypted (AES-256-GCM)
                            </td>
                          </tr>
                          <tr data-id="element-438">
                            <td className="px-4 py-3" data-id="element-439">Usage Statistics</td>
                            <td className="px-4 py-3" data-id="element-440">
                              Track API usage and costs
                            </td>
                            <td className="px-4 py-3" data-id="element-441">Stored for 90 days</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 border-l-4 border-gray-400 p-6 mb-8" data-id="element-442">
                <div className="flex items-start gap-3" data-id="element-443">
                  <Eye className="w-6 h-6 text-gray-600 flex-shrink-0 mt-1" data-id="element-444" />
                  <div data-id="element-445">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-446">
                      Data Sent to Third Parties
                    </h3>
                    <p className="text-gray-700 mb-4" data-id="element-447">
                      When using AI extraction features, page content
                      (screenshots or HTML) is processed using the Anthropic
                      Claude API:
                    </p>
                    <ul className="space-y-2 text-gray-700" data-id="element-448">
                      <li data-id="element-449">
                        <strong data-id="element-450">Anthropic (Claude AI):</strong> Receives page
                        content for cart data extraction
                      </li>
                      <li data-id="element-451">
                        Anthropic has a{' '}
                        <strong data-id="element-452">zero data retention policy</strong> for API
                        requests
                      </li>
                      <li data-id="element-453">Data is not used to train AI models</li>
                      <li data-id="element-454">
                        See{' '}
                        <a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#6366F1] hover:underline" data-id="element-455">
                          Anthropic's Privacy Policy
                        </a>
                      </li>
                    </ul>
                    <p className="text-gray-700 mt-4" data-id="element-456">
                      <strong data-id="element-457">Important:</strong> Your Anthropic API key is used
                      for these requests. You maintain direct control over your
                      API usage and can revoke access at any time.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Data We Do NOT Collect */}
            <div className="mb-12" data-id="element-458">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-459">
                Data We Do NOT Collect
              </h2>
              <ul className="space-y-2 text-gray-700" data-id="element-460">
                <li className="flex items-start gap-2" data-id="element-461">
                  <span className="text-green-500 mt-1" data-id="element-462">✓</span>
                  <span data-id="element-463">Browsing history</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-464">
                  <span className="text-green-500 mt-1" data-id="element-465">✓</span>
                  <span data-id="element-466">
                    Personal information (name, address, phone) unless visible
                    in captured cart data
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-467">
                  <span className="text-green-500 mt-1" data-id="element-468">✓</span>
                  <span data-id="element-469">
                    Payment information (credit card numbers, CVV, etc.)
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-470">
                  <span className="text-green-500 mt-1" data-id="element-471">✓</span>
                  <span data-id="element-472">Passwords or login credentials</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-473">
                  <span className="text-green-500 mt-1" data-id="element-474">✓</span>
                  <span data-id="element-475">Data from non-checkout pages</span>
                </li>
              </ul>
            </div>

            {/* How We Use Your Data */}
            <div className="mb-12" data-id="element-476">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-477">
                How We Use Your Data
              </h2>
              <ul className="space-y-2 text-gray-700" data-id="element-478">
                <li data-id="element-479">
                  <strong data-id="element-480">Purchase Tracking:</strong> Store and display your
                  captured purchase records
                </li>
                <li data-id="element-481">
                  <strong data-id="element-482">Export Functionality:</strong> Allow you to export
                  your data as JSON or CSV
                </li>
                <li data-id="element-483">
                  <strong data-id="element-484">AI Extraction:</strong> Process page content to
                  extract structured cart data (only when explicitly requested)
                </li>
                <li data-id="element-485">
                  <strong data-id="element-486">Usage Analytics:</strong> Track your API usage to
                  display costs and limits
                </li>
              </ul>
            </div>

            {/* Data Storage and Security */}
            <div className="mb-12" data-id="element-487">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-488">
                Data Storage and Security
              </h2>

              <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6" data-id="element-489">
                Local Storage
              </h3>
              <ul className="space-y-2 text-gray-700 mb-6" data-id="element-490">
                <li data-id="element-491">All purchase records are stored locally in your browser</li>
                <li data-id="element-492">
                  Data persists until you manually clear it or uninstall the
                  extension
                </li>
                <li data-id="element-493">Local data is not accessible to websites you visit</li>
              </ul>

              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-494">
                Backend Storage (When Using AI Features)
              </h3>
              <ul className="space-y-2 text-gray-700" data-id="element-495">
                <li data-id="element-496">Authentication handled by Firebase Authentication</li>
                <li data-id="element-497">API keys encrypted using AES-256-GCM before storage</li>
                <li data-id="element-498">Data transmitted over HTTPS/TLS</li>
                <li data-id="element-499">Firebase Cloud Functions for serverless processing</li>
                <li data-id="element-500">No permanent storage of page content (screenshots/HTML)</li>
              </ul>
            </div>

            {/* Your Rights and Controls */}
            <div className="mb-12" data-id="element-501">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-502">
                Your Rights and Controls
              </h2>

              <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6" data-id="element-503">
                Access Your Data
              </h3>
              <ul className="space-y-2 text-gray-700 mb-6" data-id="element-504">
                <li data-id="element-505">View all captured purchases in the extension popup</li>
                <li data-id="element-506">Export your data at any time in JSON or CSV format</li>
              </ul>

              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-507">
                Delete Your Data
              </h3>
              <ul className="space-y-2 text-gray-700 mb-6" data-id="element-508">
                <li data-id="element-509">Delete individual purchase records from the popup</li>
                <li data-id="element-510">Clear all purchase history with one click</li>
                <li data-id="element-511">Delete your account and all associated backend data</li>
              </ul>

              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-512">
                Control Data Collection
              </h3>
              <ul className="space-y-2 text-gray-700" data-id="element-513">
                <li data-id="element-514">
                  AI extraction only runs when you explicitly click "Capture
                  Cart"
                </li>
                <li data-id="element-515">Auto-capture can be disabled</li>
                <li data-id="element-516">
                  You can use the extension without creating an account
                  (local-only mode)
                </li>
              </ul>
            </div>

            {/* Permissions Explained */}
            <div className="mb-12" data-id="element-517">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-518">
                Permissions Explained
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-519">
                The extension requests the following permissions:
              </p>

              <div className="overflow-x-auto" data-id="element-520">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg" data-id="element-521">
                  <thead className="bg-gray-50" data-id="element-522">
                    <tr data-id="element-523">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b" data-id="element-524">
                        Permission
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b" data-id="element-525">
                        Why We Need It
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-sm text-gray-700" data-id="element-526">
                    <tr className="border-b" data-id="element-527">
                      <td className="px-4 py-3 font-mono text-xs" data-id="element-528">storage</td>
                      <td className="px-4 py-3" data-id="element-529">
                        Save your purchase records and preferences locally
                      </td>
                    </tr>
                    <tr className="border-b" data-id="element-530">
                      <td className="px-4 py-3 font-mono text-xs" data-id="element-531">activeTab</td>
                      <td className="px-4 py-3" data-id="element-532">
                        Access the current tab to capture cart data when you
                        click capture
                      </td>
                    </tr>
                    <tr className="border-b" data-id="element-533">
                      <td className="px-4 py-3 font-mono text-xs" data-id="element-534">scripting</td>
                      <td className="px-4 py-3" data-id="element-535">
                        Inject content scripts to read cart data from checkout
                        pages
                      </td>
                    </tr>
                    <tr data-id="element-536">
                      <td className="px-4 py-3 font-mono text-xs" data-id="element-537">
                        host_permissions (all URLs)
                      </td>
                      <td className="px-4 py-3" data-id="element-538">
                        Allow cart capture from any e-commerce website
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mt-6" data-id="element-539">
                <div className="flex items-start gap-3" data-id="element-540">
                  <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" data-id="element-541" />
                  <div data-id="element-542">
                    <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-543">
                      Why All-Sites Permission?
                    </h3>
                    <p className="text-gray-700 mb-2" data-id="element-544">
                      We request access to all websites because:
                    </p>
                    <ol className="list-decimal list-inside space-y-1 text-gray-700" data-id="element-545">
                      <li data-id="element-546">
                        E-commerce sites vary widely - there's no comprehensive
                        list
                      </li>
                      <li data-id="element-547">
                        We only activate on checkout pages you explicitly
                        capture
                      </li>
                      <li data-id="element-548">
                        The extension does NOT run background tracking on all
                        pages
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            {/* Data Retention */}
            <div className="mb-12" data-id="element-549">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-550">
                Data Retention
              </h2>
              <div className="overflow-x-auto" data-id="element-551">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg" data-id="element-552">
                  <thead className="bg-gray-50" data-id="element-553">
                    <tr data-id="element-554">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b" data-id="element-555">
                        Data Type
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b" data-id="element-556">
                        Retention Period
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-sm text-gray-700" data-id="element-557">
                    <tr className="border-b" data-id="element-558">
                      <td className="px-4 py-3" data-id="element-559">Local purchase records</td>
                      <td className="px-4 py-3" data-id="element-560">Until you delete them</td>
                    </tr>
                    <tr className="border-b" data-id="element-561">
                      <td className="px-4 py-3" data-id="element-562">Account information</td>
                      <td className="px-4 py-3" data-id="element-563">Until account deletion</td>
                    </tr>
                    <tr className="border-b" data-id="element-564">
                      <td className="px-4 py-3" data-id="element-565">Encrypted API keys</td>
                      <td className="px-4 py-3" data-id="element-566">
                        Until you delete them or delete your account
                      </td>
                    </tr>
                    <tr className="border-b" data-id="element-567">
                      <td className="px-4 py-3" data-id="element-568">Usage statistics</td>
                      <td className="px-4 py-3" data-id="element-569">90 days</td>
                    </tr>
                    <tr data-id="element-570">
                      <td className="px-4 py-3" data-id="element-571">
                        Page content (screenshots/HTML)
                      </td>
                      <td className="px-4 py-3" data-id="element-572">
                        Not stored (processed and discarded)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Children's Privacy */}
            <div className="mb-12" data-id="element-573">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-574">
                Children's Privacy
              </h2>
              <p className="text-gray-700" data-id="element-575">
                This extension is not intended for use by children under 13
                years of age. We do not knowingly collect personal information
                from children.
              </p>
            </div>

            {/* Changes to This Policy */}
            <div className="mb-12" data-id="element-576">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-577">
                Changes to This Policy
              </h2>
              <p className="text-gray-700" data-id="element-578">
                We may update this privacy policy from time to time. We will
                notify users of any material changes by updating the "Last
                Updated" date and, where appropriate, through the extension
                itself.
              </p>
            </div>

            {/* Contact Us */}
            <div className="mb-12" data-id="element-579">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-580">
                Contact Us
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-581">
                If you have questions about this privacy policy or your data:
              </p>
              <ul className="space-y-2 text-gray-700" data-id="element-582">
                <li data-id="element-583">
                  <strong data-id="element-584">Email:</strong>{' '}
                  <a href="mailto:support@carttracker.com" className="text-[#6366F1] hover:underline" data-id="element-585">
                    alex@thruline.co
                  </a>
                </li>
              </ul>
            </div>

            {/* Consent */}
            <div className="mb-12" data-id="element-586">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-587">Consent</h2>
              <p className="text-gray-700" data-id="element-588">
                By installing and using Cart Tracker, you consent to the data
                practices described in this policy. For AI extraction features
                specifically, you provide additional consent each time you click
                the capture button.
              </p>
            </div>

            {/* Summary */}
            <div className="bg-gradient-to-br from-[#EEF2FF] to-[#F5F3FF] rounded-2xl p-8" data-id="element-589">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-590">
                Summary
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-591">
                <div className="flex items-start gap-3" data-id="element-592">
                  <div className="w-8 h-8 rounded-full bg-[#6366F1] flex items-center justify-center flex-shrink-0" data-id="element-593">
                    <Database className="w-4 h-4 text-white" data-id="element-594" />
                  </div>
                  <div data-id="element-595">
                    <h3 className="font-bold text-gray-900 mb-1" data-id="element-596">
                      Local-only by default
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-597">
                      Your purchase data stays in your browser
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-598">
                  <div className="w-8 h-8 rounded-full bg-[#6366F1] flex items-center justify-center flex-shrink-0" data-id="element-599">
                    <Lock className="w-4 h-4 text-white" data-id="element-600" />
                  </div>
                  <div data-id="element-601">
                    <h3 className="font-bold text-gray-900 mb-1" data-id="element-602">
                      AI features are opt-in
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-603">
                      You control when data is sent for processing
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-604">
                  <div className="w-8 h-8 rounded-full bg-[#6366F1] flex items-center justify-center flex-shrink-0" data-id="element-605">
                    <Eye className="w-4 h-4 text-white" data-id="element-606" />
                  </div>
                  <div data-id="element-607">
                    <h3 className="font-bold text-gray-900 mb-1" data-id="element-608">
                      No tracking
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-609">
                      We don't track your browsing or sell your data
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-610">
                  <div className="w-8 h-8 rounded-full bg-[#6366F1] flex items-center justify-center flex-shrink-0" data-id="element-611">
                    <FileText className="w-4 h-4 text-white" data-id="element-612" />
                  </div>
                  <div data-id="element-613">
                    <h3 className="font-bold text-gray-900 mb-1" data-id="element-614">
                      Transparent
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-615">
                      Export or delete your data at any time
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-616">
                  <div className="w-8 h-8 rounded-full bg-[#6366F1] flex items-center justify-center flex-shrink-0" data-id="element-617">
                    <Shield className="w-4 h-4 text-white" data-id="element-618" />
                  </div>
                  <div data-id="element-619">
                    <h3 className="font-bold text-gray-900 mb-1" data-id="element-620">Secure</h3>
                    <p className="text-sm text-gray-600" data-id="element-621">
                      API keys encrypted, all traffic over HTTPS
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </div>;
}